import React, { useState, useEffect, useCallback } from 'react';
import { artistInfo } from '../data/mock';
import { Music2, Headphones, Mic, Sparkles, ChevronLeft, ChevronRight, X } from 'lucide-react';

const About = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  const portfolioPhotos = [
    '/media/slider-IMG_7271.png',
    '/media/slider-IMG_7615-1.png',
    '/media/slider-IMG_7632.png',
    '/media/slider-IMG_7614-2.png',
    '/media/slider-IMG_7613-3.png',
    '/media/slider-IMG_7608-1.png',
    '/media/slider-IMG_7599.png'
  ];

  // Ouvrir la lightbox
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Fermer la lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  // Navigation lightbox
  const nextLightbox = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % portfolioPhotos.length);
  }, [portfolioPhotos.length]);

  const prevLightbox = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + portfolioPhotos.length) % portfolioPhotos.length);
  }, [portfolioPhotos.length]);

  // Gestion clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextLightbox();
      if (e.key === 'ArrowLeft') prevLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, nextLightbox, prevLightbox]);

  const highlights = [
    { icon: Music2, label: 'Production', value: '10+ ans' },
    { icon: Headphones, label: 'Beats créés', value: '50+' },
    { icon: Mic, label: 'Collaborations', value: '30+' },
    { icon: Sparkles, label: 'Projets', value: '50+' }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % portfolioPhotos.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + portfolioPhotos.length) % portfolioPhotos.length);
  };

  return (
    <section id="about" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-black to-neutral-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image principale + Slider */}
          <div className="space-y-6">
            {/* Image principale */}
            <div className="relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl lg:rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
              <img
                src={artistInfo.image}
                alt={artistInfo.name}
                className="relative rounded-xl lg:rounded-2xl w-full h-[400px] sm:h-[450px] lg:h-[500px] object-cover shadow-2xl"
              />
            </div>

            {/* Slider Portfolio Photos horizontal avec flèches */}
            <div className="relative">
              {/* Flèche Gauche */}
              <button
                onClick={prevSlide}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black/70 hover:bg-rose-800 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg"
              >
                <ChevronLeft size={20} className="text-white" />
              </button>

              {/* Flèche Droite */}
              <button
                onClick={nextSlide}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black/70 hover:bg-rose-800 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg"
              >
                <ChevronRight size={20} className="text-white" />
              </button>

              {/* Scroll horizontal */}
              <div className="overflow-hidden px-8 sm:px-12">
                <div 
                  className="flex gap-3 sm:gap-4 transition-transform duration-300 ease-out"
                  style={{ transform: `translateX(-${currentSlide * (12 * 16 + 16)}px)` }}
                >
                  {portfolioPhotos.map((photo, index) => (
                    <div 
                      key={index} 
                      className="flex-shrink-0 w-40 h-40 sm:w-48 sm:h-48 rounded-lg sm:rounded-xl overflow-hidden cursor-pointer shadow-lg"
                      onClick={() => openLightbox(index)}
                    >
                      <img
                        src={photo}
                        alt={`Portfolio ${index + 1}`}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out hover:scale-110"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase text-white mb-4 sm:mb-6 tracking-wide">
              À Propos
            </h2>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
              {artistInfo.bio}
            </p>
            <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-6 sm:mb-8">
              De la scène aux studios d'enregistrement, je transforme des idées en expériences sonores mémorables. Mon approche combine technique pointue, sensibilité artistique et vision créative pour donner vie à des projets musicaux uniques.
            </p>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6 mb-8">
              {highlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div
                    key={index}
                    className="bg-neutral-800/50 backdrop-blur-sm border border-neutral-700 rounded-lg sm:rounded-xl p-4 sm:p-6 hover:border-violet-500/50 transition-all duration-300 hover:transform hover:scale-105"
                  >
                    <Icon className="text-violet-400 mb-2 sm:mb-3" size={28} />
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{item.value}</div>
                    <div className="text-gray-400 text-xs sm:text-sm uppercase tracking-wider">{item.label}</div>
                  </div>
                );
              })}
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <a
                href={artistInfo.social.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-rose-900 to-rose-700 hover:from-rose-800 hover:to-rose-600 text-white rounded-full text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-rose-500/50"
              >
                Spotify
              </a>
              <a
                href={artistInfo.social.soundcloud}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-violet-900 to-rose-800 hover:from-violet-800 hover:to-rose-700 text-white rounded-full text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-violet-500/50"
              >
                SoundCloud
              </a>
              <a
                href={artistInfo.social.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-rose-800 to-violet-800 hover:from-rose-700 hover:to-violet-700 text-white rounded-full text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-rose-500/50"
              >
                YouTube
              </a>
              <a
                href={artistInfo.social.appleMusic}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-rose-900 to-violet-900 hover:from-rose-800 hover:to-violet-800 text-white rounded-full text-sm sm:text-base font-medium transition-all duration-300 shadow-lg hover:shadow-violet-500/50"
              >
                Apple Music
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Bouton Fermer */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 sm:top-8 sm:right-8 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors duration-300 z-50"
          >
            <X size={24} className="text-white" />
          </button>

          {/* Navigation Gauche */}
          <button
            onClick={(e) => { e.stopPropagation(); prevLightbox(); }}
            className="absolute left-2 sm:left-8 top-1/2 transform -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-rose-800 rounded-full flex items-center justify-center transition-all duration-300 z-50"
          >
            <ChevronLeft size={28} className="text-white" />
          </button>

          {/* Navigation Droite */}
          <button
            onClick={(e) => { e.stopPropagation(); nextLightbox(); }}
            className="absolute right-2 sm:right-8 top-1/2 transform -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 bg-white/10 hover:bg-rose-800 rounded-full flex items-center justify-center transition-all duration-300 z-50"
          >
            <ChevronRight size={28} className="text-white" />
          </button>

          {/* Image principale */}
          <div 
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={portfolioPhotos[lightboxIndex]}
              alt={`Portfolio ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Compteur */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-black/60 rounded-full text-white text-sm">
              {lightboxIndex + 1} / {portfolioPhotos.length}
            </div>
          </div>

          {/* Miniatures en bas */}
          <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2 sm:gap-3 max-w-[90vw] overflow-x-auto px-4 pb-2">
            {portfolioPhotos.map((photo, index) => (
              <button
                key={index}
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(index); }}
                className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden transition-all duration-300 ${
                  index === lightboxIndex 
                    ? 'ring-2 ring-rose-500 scale-110' 
                    : 'opacity-50 hover:opacity-100'
                }`}
              >
                <img
                  src={photo}
                  alt={`Miniature ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default About;